import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk } from "appThunk";
import { RootState } from "store";
import { Partner, PartnerStatus } from "modules/customer/tools/product/partner";
import { Operator, OperatorStatus } from "modules/customer/tools/product/operator";
import { selectOperators, selectPartners } from "modules/customer/tools/product/productSlice";

import mathUtils from "utils/mathUtils";
import { SortDirection, numberSortExpression, stringSortExpression } from "utils/sortUtils";

interface FiltersVisibility {
    isVisible: boolean
}

interface SliderThresholds {
    minPercentileThreshold: number,
    maxPercentileThreshold: number,
    percentileThresholds: number[]
}

export enum PartnerFilterStep {
    SelectPartner,
    SelectOperator
}

export interface PartnersSearch {
    name: string
}

export interface PartnersFilter {
    status: PartnerStatus | string,
    headroom: number[],
    optimisedSales: number[],
    totalEstimatedSales: number[],
    clientSourcedSales: number[]
}

export enum PartnersSortField {
    PartnerName,
    Headroom,
    OptimisedSales,
    TotalEstimatedSales,
    ClientSourcedSales
}

interface PartnersSort {
    field: PartnersSortField,
    direction: SortDirection
}

export interface OperatorsSearch {
    name: string
}

export interface OperatorsFilter {
    status: OperatorStatus | string,
    headroom: number[],
    optimisedSales: number[],
    totalEstimatedSales: number[],
    clientSourcedSales: number[]
}

export enum OperatorsSortField {
    OperatorName,
    Headroom,
    OptimisedSales,
    TotalEstimatedSales,
    ClientSourcedSales
}

interface OperatorsSort {
    field: OperatorsSortField,
    direction: SortDirection
}

interface FiltersState {
    partnerFiltersVisibility: FiltersVisibility,
    activeStep: PartnerFilterStep,
    partnersSearch: PartnersSearch,
    candidatePartner?: Partner,
    candidatePartnerGroup?: string,
    partnersFilter: PartnersFilter,
    partnersSort: PartnersSort,
    candidateOperator?: Operator,
    operatorsSearch: OperatorsSearch,
    operatorsFilter: OperatorsFilter,
    operatorsSort: OperatorsSort
}

const initialState: FiltersState = {
    partnerFiltersVisibility: {
        isVisible: false
    },
    activeStep: PartnerFilterStep.SelectPartner,
    partnersSearch: {
        name: ""
    },
    candidatePartner: undefined,
    candidatePartnerGroup: undefined,
    partnersFilter: {
        status: "",
        headroom: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        optimisedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        totalEstimatedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        clientSourcedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY]
    },
    partnersSort: {
        field: PartnersSortField.PartnerName,
        direction: SortDirection.ASC
    },
    candidateOperator: undefined,
    operatorsSearch: {
        name: ""
    },
    operatorsFilter: {
        status: "",
        headroom: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        optimisedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        totalEstimatedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY],
        clientSourcedSales: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY]
    },
    operatorsSort: {
        field: OperatorsSortField.OperatorName,
        direction: SortDirection.ASC
    }
};

const partnerFiltersSlice = createSlice({
    name: "customer/tools/product/partnerFilters",
    initialState,
    reducers: {
        showPartnerFilters: (state) => {
            state.activeStep = initialState.activeStep;
            state.partnerFiltersVisibility.isVisible = true;
        },
        hidePartnerFilters: (state) => {
            state.partnerFiltersVisibility.isVisible = false;
        },
        setActiveStep: (state, action: PayloadAction<PartnerFilterStep>) => {
            state.activeStep = action.payload;
        },
        clearActiveStep: (state) => {
            state.activeStep = initialState.activeStep;
        },
        setPartnersSearch: (state, action: PayloadAction<PartnersSearch>) => {
            state.partnersSearch = action.payload;
        },
        clearPartnersSearch: (state) => {
            state.partnersSearch = initialState.partnersSearch;
        },
        setCandidatePartner: (state, action: PayloadAction<Partner>) => {
            state.candidatePartner = action.payload;
        },
        clearCandidatePartner: (state) => {
            state.candidatePartner = initialState.candidatePartner;
        },
        setCandidatePartnerGroup: (state, action: PayloadAction<string>) => {
            state.candidatePartnerGroup = action.payload;
        },
        clearCandidatePartnerGroup: (state) => {
            state.candidatePartnerGroup = initialState.candidatePartnerGroup;
        },
        setPartnersFilter: (state, action: PayloadAction<PartnersFilter>) => {
            state.partnersFilter = action.payload;
        },
        clearPartnersFilter: (state) => {
            state.partnersFilter = initialState.partnersFilter;
        },
        setPartnersSort: (state, action: PayloadAction<PartnersSort>) => {
            state.partnersSort = action.payload;
        },
        clearPartnersSort: (state) => {
            state.partnersSort = initialState.partnersSort;
        },
        setCandidateOperator: (state, action: PayloadAction<Operator>) => {
            state.candidateOperator = action.payload;
        },
        clearCandidateOperator: (state) => {
            state.candidateOperator = initialState.candidateOperator;
        },
        setOperatorsSearch: (state, action: PayloadAction<OperatorsSearch>) => {
            state.operatorsSearch = action.payload;
        },
        clearOperatorsSearch: (state) => {
            state.operatorsSearch = initialState.operatorsSearch;
        },
        setOperatorsFilter: (state, action: PayloadAction<OperatorsFilter>) => {
            state.operatorsFilter = action.payload;
        },
        clearOperatorsFilter: (state) => {
            state.operatorsFilter = initialState.operatorsFilter;
        },
        setOperatorsSort: (state, action: PayloadAction<OperatorsSort>) => {
            state.operatorsSort = action.payload;
        },
        clearOperatorsSort: (state) => {
            state.operatorsSort = initialState.operatorsSort;
        },
    },
    extraReducers: (builder: any) => { }
});

export const {
    showPartnerFilters,
    hidePartnerFilters,
    setActiveStep,
    setPartnersSearch,
    setCandidatePartner,
    setCandidatePartnerGroup,
    setOperatorsSearch,
    setOperatorsFilter,
    setOperatorsSort,
    clearActiveStep,
    clearCandidatePartner,
    clearCandidatePartnerGroup,
    clearPartnersSearch,
    setPartnersFilter,
    clearPartnersFilter,
    setPartnersSort,
    clearPartnersSort,
    setCandidateOperator,
    clearCandidateOperator,
    clearOperatorsSearch,
    clearOperatorsFilter,
    clearOperatorsSort
} = partnerFiltersSlice.actions;

export const clearPartnerFilters = (): AppThunk => async (dispatch) => {
    dispatch(partnerFiltersSlice.actions.hidePartnerFilters());
    dispatch(partnerFiltersSlice.actions.clearActiveStep());
    dispatch(partnerFiltersSlice.actions.clearPartnersSearch());
    dispatch(partnerFiltersSlice.actions.clearCandidatePartner());
    dispatch(partnerFiltersSlice.actions.clearCandidatePartnerGroup());
    dispatch(partnerFiltersSlice.actions.clearPartnersFilter());
    dispatch(partnerFiltersSlice.actions.clearPartnersSort());
    dispatch(partnerFiltersSlice.actions.clearCandidateOperator());
    dispatch(partnerFiltersSlice.actions.clearOperatorsSearch());
    dispatch(partnerFiltersSlice.actions.clearOperatorsFilter());
    dispatch(partnerFiltersSlice.actions.clearOperatorsSort());
};

export const selectPartnerFiltersVisibility = (state: RootState): FiltersVisibility => {
    return state.customer.tools.product.partnerFilters.partnerFiltersVisibility;
};

export const selectActiveStep = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.activeStep;
};

export const selectPartnersSearch = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.partnersSearch;
};

export const selectCandidatePartner = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.candidatePartner;
};

export const selectCandidatePartnerGroup = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.candidatePartnerGroup;
};

export const selectPartnersFilter = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.partnersFilter;
};

export const selectPartnersSort = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.partnersSort;
};

export const selectCandidateOperator = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.candidateOperator;
};

export const selectOperatorsSearch = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.operatorsSearch;
};

export const selectOperatorsFilter = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.operatorsFilter;
};

export const selectOperatorsSort = (state: RootState) => {
    return state.customer.tools.product.partnerFilters.operatorsSort;
};

export const selectPartnerStatuses = createSelector(
    (state: RootState) => selectPartners(state),
    (partners) => {
        const statuses = partners
            .map(partner => partner.status)
            .sort((a, b) => stringSortExpression(a, b, SortDirection.ASC));
        return Array.from(new Set(statuses));
    }
);

export const selectOperatorStatuses = createSelector(
    (state: RootState) => selectOperators(state),
    (operators) => {
        const statuses = operators.data
            .map(operator => operator.status)
            .sort((a, b) => stringSortExpression(a, b, SortDirection.ASC));
        return Array.from(new Set(statuses));
    }
);

const generateSliderThresholds = (values: number[]): SliderThresholds => {
    const percentileThresholds = mathUtils.percentileThresholds(values, 5);
    percentileThresholds.push(values[(values.length - 1)]);
    return {
        minPercentileThreshold: percentileThresholds[0],
        maxPercentileThreshold: percentileThresholds[percentileThresholds.length - 1],
        percentileThresholds
    };
};

export const selectPartnersHeadroom = createSelector(
    (state: RootState) => selectPartners(state),
    (partners) => {
        const headroom = partners
            .map(partner => partner.sales.salesHeadroom)
            .sort((a, b) => a - b);
        return generateSliderThresholds(headroom);
    }
);

export const selectPartnersOptimisedSales = createSelector(
    (state: RootState) => selectPartners(state),
    (partners) => {
        const optimisedSales = partners
            .map(partner => partner.sales.optimisedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(optimisedSales);
    }
);

export const selectPartnersTotalEstimatedSales = createSelector(
    (state: RootState) => selectPartners(state),
    (partners) => {
        const headroom = partners
            .map(partner => partner.sales.estimatedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(headroom);
    }
);

export const selectPartnersClientSourcedSales = createSelector(
    (state: RootState) => selectPartners(state),
    (partners) => {
        const clientSourcedSales = partners
            .map(partner => partner.sales.clientSourcedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(clientSourcedSales);
    }
);

export const selectOperatorsHeadroom = createSelector(
    (state: RootState) => selectOperators(state),
    (operators) => {
        const headroom = operators.data
            .map(operator => operator.sales.salesHeadroom)
            .sort((a, b) => a - b);
        return generateSliderThresholds(headroom);
    }
);

export const selectOperatorsOptimisedSales = createSelector(
    (state: RootState) => selectOperators(state),
    (operators) => {
        const optimisedSales = operators.data
            .map(operator => operator.sales.optimisedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(optimisedSales);
    }
);

export const selectOperatorsTotalEstimatedSales = createSelector(
    (state: RootState) => selectOperators(state),
    (operators) => {
        const estimatedSales = operators.data
            .map(operator => operator.sales.estimatedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(estimatedSales);
    }
);

export const selectOperatorsClientSourcedSales = createSelector(
    (state: RootState) => selectOperators(state),
    (operators) => {
        const clientSourcedSales = operators.data
            .map(operator => operator.sales.clientSourcedSales)
            .sort((a, b) => a - b);
        return generateSliderThresholds(clientSourcedSales);
    }
);

export const selectIsPartnersFilterModified = createSelector(
    selectPartnersFilter,
    (partnersFilter) => {
        return partnersFilter.status !== initialState.partnersFilter.status
            || partnersFilter.headroom[0] !== initialState.partnersFilter.headroom[0]
            || partnersFilter.headroom[1] !== initialState.partnersFilter.headroom[1]
            || partnersFilter.optimisedSales[0] !== initialState.partnersFilter.optimisedSales[0]
            || partnersFilter.optimisedSales[1] !== initialState.partnersFilter.optimisedSales[1]
            || partnersFilter.totalEstimatedSales[0] !== initialState.partnersFilter.totalEstimatedSales[0]
            || partnersFilter.totalEstimatedSales[1] !== initialState.partnersFilter.totalEstimatedSales[1]
            || partnersFilter.clientSourcedSales[0] !== initialState.partnersFilter.clientSourcedSales[0]
            || partnersFilter.clientSourcedSales[1] !== initialState.partnersFilter.clientSourcedSales[1];
    }
);

export const selectFilteredPartners = createSelector(
    (state: RootState) => selectPartners(state),
    selectPartnersSearch,
    selectPartnersFilter,
    selectPartnersSort,
    (partners, search, filter, sort) => {
        const searchString = search.name.toLowerCase();
        const filteredPartners = partners.filter(partner =>
            (!searchString || partner.name.toLowerCase().includes(searchString))
            && (!filter.status || partner.status === filter.status)
            && partner.sales.salesHeadroom >= filter.headroom[0]
            && partner.sales.salesHeadroom <= filter.headroom[1]
            && partner.sales.optimisedSales >= filter.optimisedSales[0]
            && partner.sales.optimisedSales <= filter.optimisedSales[1]
            && partner.sales.estimatedSales >= filter.totalEstimatedSales[0]
            && partner.sales.estimatedSales <= filter.totalEstimatedSales[1]
            && partner.sales.clientSourcedSales >= filter.clientSourcedSales[0]
            && partner.sales.clientSourcedSales <= filter.clientSourcedSales[1] 
        );

        return filteredPartners.sort((a, b) => {
            switch (sort.field) {
                case PartnersSortField.Headroom:
                    return numberSortExpression(a.sales.salesHeadroom, b.sales.salesHeadroom, sort.direction);
                case PartnersSortField.OptimisedSales:
                    return numberSortExpression(a.sales.optimisedSales, b.sales.optimisedSales, sort.direction);
                case PartnersSortField.TotalEstimatedSales:
                    return numberSortExpression(a.sales.estimatedSales, b.sales.estimatedSales, sort.direction);
                case PartnersSortField.ClientSourcedSales:
                    return numberSortExpression(a.sales.clientSourcedSales, b.sales.clientSourcedSales, sort.direction);
                case PartnersSortField.PartnerName:
                default:
                    return stringSortExpression(a.name, b.name, sort.direction);
            }
        });
    }
);

export const selectIsOperatorsFilterModified = createSelector(
    selectOperatorsFilter,
    (operatorsFilter) => {
        return operatorsFilter.status !== initialState.operatorsFilter.status
            || operatorsFilter.headroom[0] !== initialState.operatorsFilter.headroom[0]
            || operatorsFilter.headroom[1] !== initialState.operatorsFilter.headroom[1]
            || operatorsFilter.optimisedSales[0] !== initialState.operatorsFilter.optimisedSales[0]
            || operatorsFilter.optimisedSales[1] !== initialState.operatorsFilter.optimisedSales[1]
            || operatorsFilter.totalEstimatedSales[0] !== initialState.operatorsFilter.totalEstimatedSales[0]
            || operatorsFilter.totalEstimatedSales[1] !== initialState.operatorsFilter.totalEstimatedSales[1]
            || operatorsFilter.clientSourcedSales[0] !== initialState.operatorsFilter.clientSourcedSales[0]
            || operatorsFilter.clientSourcedSales[1] !== initialState.operatorsFilter.clientSourcedSales[1];
    }
);

export const selectFilteredOperators = createSelector(
    (state: RootState) => selectOperators(state),
    selectOperatorsSearch,
    selectOperatorsFilter,
    selectOperatorsSort,
    (operators, search, filter, sort) => {
        const searchString = search.name.toLowerCase();
        const filteredOperators = operators.data.filter(operator =>
            (!searchString || operator.name.toLowerCase().includes(searchString))
            && (!filter.status || operator.status === filter.status)
            && operator.sales.salesHeadroom >= filter.headroom[0]
            && operator.sales.salesHeadroom <= filter.headroom[1]
            && operator.sales.optimisedSales >= filter.optimisedSales[0]
            && operator.sales.optimisedSales <= filter.optimisedSales[1]
            && operator.sales.estimatedSales >= filter.totalEstimatedSales[0]
            && operator.sales.estimatedSales <= filter.totalEstimatedSales[1]
            && operator.sales.clientSourcedSales >= filter.clientSourcedSales[0]
            && operator.sales.clientSourcedSales <= filter.clientSourcedSales[1]
        );

        return filteredOperators.sort((a, b) => {
            switch (sort.field) {
                case OperatorsSortField.Headroom:
                    return numberSortExpression(a.sales.salesHeadroom, b.sales.salesHeadroom, sort.direction);
                case OperatorsSortField.OptimisedSales:
                    return numberSortExpression(a.sales.optimisedSales, b.sales.optimisedSales, sort.direction);
                case OperatorsSortField.TotalEstimatedSales:
                    return numberSortExpression(a.sales.estimatedSales, b.sales.estimatedSales, sort.direction);
                case OperatorsSortField.ClientSourcedSales:
                    return numberSortExpression(a.sales.clientSourcedSales, b.sales.clientSourcedSales, sort.direction);
                case OperatorsSortField.OperatorName:
                default:
                    return stringSortExpression(a.name, b.name, sort.direction);
            }
        });
    }
);

export default partnerFiltersSlice;
